/**
 * The back stack (docs/design/visual-overhaul.md §12): on phones and tablets
 * every pane, drawer, sheet and dialog that a Back gesture should close
 * pushes itself here. Android's back button, iOS's swipe-back and the
 * browser's Back go back one step, as Discord's mobile app does.
 *
 * It keeps one history entry of its own (a guard) while anything is on the
 * stack: Back moves off the guard (popstate), the top layer closes, and the
 * guard is put back while more remain. A layer closed by the app itself
 * (its close button, a tap outside, Escape) leaves the stack; when the
 * stack empties, the guard is taken off history again. On the desktop
 * layout nothing touches history: Escape and the close buttons do it all.
 */
import { readLayout } from './layout';

interface Entry {
  id: number;
  onBack: () => void;
}

const stack: Entry[] = [];
let seq = 0;
/** Our guard entry is the current history entry. */
let guarded = false;
/** `setHash` gave the guard a new address: it is now a real step, not ours to take back. */
let guardMoved = false;
/** history.back() calls of our own whose popstate has not come yet. */
let skipping = 0;
/** A hash `setHash` set whose popstate (if the browser sends one) has not come yet. */
let ownHash: string | null = null;
/** An address `setHash` was given while our own Back was on its way: set once it lands. */
let pendingHash: string | null = null;

const historyOn = (): boolean =>
  typeof history !== 'undefined' &&
  typeof history.pushState === 'function' &&
  readLayout().layout !== 'desktop';

function ensureGuard(): void {
  if (!stack.length || guarded || skipping || !historyOn()) return;
  try {
    history.pushState({ ...(history.state ?? {}), nxBack: true }, '');
    guarded = true;
    guardMoved = false;
  } catch {
    /* a sandboxed frame: Back stays the app's own buttons */
  }
}

function dropGuard(): void {
  if (!guarded) return;
  guarded = false;
  if (guardMoved) {
    // the guard holds the address the app moved to (a menu choice, a
    // drawer's close): going back from it would land on the old address,
    // and the router would follow it there. It stays, as a plain entry.
    guardMoved = false;
    try {
      const { nxBack: _ours, ...rest } = (history.state ?? {}) as Record<string, unknown>;
      history.replaceState(rest, '');
    } catch {
      /* the entry keeps its mark: a later popstate onto it is not ours */
    }
    return;
  }
  skipping++;
  history.back();
}

/**
 * Puts a layer on the back stack: `onBack` runs when Back is pressed while it
 * is on top. Returns the call to make when the layer closes by itself.
 */
export function pushBack(onBack: () => void): () => void {
  const e: Entry = { id: ++seq, onBack };
  stack.push(e);
  ensureGuard();
  return () => {
    const i = stack.indexOf(e);
    if (i < 0) return;
    stack.splice(i, 1);
    if (!stack.length) dropGuard();
  };
}

/** How many layers are on the stack. */
export function backDepth(): number {
  return stack.length;
}

/**
 * Goes back one step, as the system's Back would (the app's own Back
 * buttons, the edge swipe). False when there is nothing to go back to.
 */
export function goBack(): boolean {
  if (!stack.length) return false;
  if (guarded) {
    // the browser's own Back: popstate closes the top layer
    history.back();
    return true;
  }
  stack.pop()?.onBack();
  return true;
}

/**
 * Moves to an in-app address (`#/...`) without it reading as Back. While a
 * layer holds the guard entry, the guard takes the new address (a new entry
 * there would bury the guard, and its popstate would close the layer); Back
 * still closes the layer. When the layer closes by itself after that, the
 * guard stays as the new address's entry, so the app stays where it went.
 * While our own Back is on its way (the last layer just closed), the
 * address is set once it lands. Otherwise a normal hash change, whose
 * popstate is not taken for Back even when a layer opened before it came.
 * No hashchange follows a replaced guard: the caller updates its route itself.
 */
export function setHash(href: string): void {
  if (skipping) {
    pendingHash = href;
    return;
  }
  if (location.hash === href) return;
  if (guarded) {
    try {
      history.replaceState(history.state, '', href);
      guardMoved = true;
      return;
    } catch {
      /* fall through to a normal hash change */
    }
  }
  ownHash = href;
  location.hash = href;
}

if (typeof window !== 'undefined') {
  addEventListener('hashchange', () => {
    ownHash = null;
  });
  addEventListener('popstate', () => {
    if (ownHash !== null && location.hash === ownHash) {
      // our own hash change, not Back
      ownHash = null;
      return;
    }
    if (skipping) {
      skipping--;
      if (!skipping && pendingHash !== null) {
        const href = pendingHash;
        pendingHash = null;
        setHash(href);
      }
      ensureGuard();
      return;
    }
    if (!guarded) return;
    guarded = false;
    guardMoved = false;
    stack.pop()?.onBack();
    ensureGuard();
  });
}
